/**
 * ScResearchPreviewCard (026, US3) — preview of the seed research areas that
 * will run after scope lock. Presentational only.
 */

import { Search } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ChatCard } from '../../widgets/chat-widget-ui';
import { capitalizeFirst } from '../../widgets/textFormat';
import { SkBar } from '../../widgets/skeletons';
import { ScStateBadge } from './ScStateBadge';
import type { ScResearchPreviewCardProps } from '@shared/widget-schemas';

interface ScResearchPreviewCardComponentProps extends ScResearchPreviewCardProps {
  className?: string;
}

export function ScResearchPreviewCard({ areas, estimate, draft, className }: ScResearchPreviewCardComponentProps) {
  return (
    <ChatCard className={cn('flex flex-col gap-3 p-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Search className="size-3.5 text-[#C084FC]" aria-hidden="true" />
          <span className="text-muted-foreground font-mono text-[10.5px] font-bold uppercase tracking-wide">
            Research areas
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ScStateBadge draft={draft} />
          {estimate && (
            <span className="rounded-full border border-[#7C3AED]/30 px-2 py-0.5 font-mono text-[9.5px] font-semibold text-[#C084FC]">
              {estimate}
            </span>
          )}
        </div>
      </div>

      {/* Area rows */}
      <ul className="flex flex-col gap-2">
        {areas.map((area, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span className="mt-0.5 text-[#C084FC]">·</span>
            <div className="flex flex-col gap-0.5">
              <span className="text-foreground text-[13px] font-medium">{capitalizeFirst(area.label)}</span>
              {area.sub && (
                <span className="text-muted-foreground text-[11.5px]">{capitalizeFirst(area.sub)}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </ChatCard>
  );
}

export function ScResearchPreviewCardSkeleton({ className }: { className?: string }) {
  return (
    <ChatCard className={cn('flex flex-col gap-3 p-4', className)}>
      <div className="flex justify-between">
        <SkBar width="35%" height="10px" />
        <SkBar width="15%" height="10px" />
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex flex-col gap-1">
          <SkBar width="45%" height="12px" />
          <SkBar width="65%" height="9px" />
        </div>
      ))}
    </ChatCard>
  );
}
